import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import {
    PANEL_CATEGORIES,
    LINE_CHART_PANEL_PREVIEW_IMAGE,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { useMetricColor } from '../../../utilities/ColorUtilities';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { radioFilterId, timeRangeFilterId } from '../../filters';

const title = t`Radio Channel Utilization`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'radio',
        metrics: ['channelUtilization', 'channelUtilizationStatus'],
    },
};

export function useChartConfig() {
    const color = useMetricColor('radioChannelUtil');

    return useMemo(
        () => ({
            yAxis: [
                {
                    title: {
                        text: t`Channel Utilization (%)`,
                    },
                    ceiling: 100,
                },
            ],
            tooltip: BuiltInTooltip,
            showGranularity: true,
            statusMetric: 'channelUtilizationStatus',
            chartTrends: [
                {
                    formatter: FORMATTER_TYPES.PERCENT,
                    forceDecimals: true,
                    units: '%',
                    chartType: 'spline',
                    key: 'channelUtilization',
                    label: t`Channel Utilization`,
                    color,
                    multiplier: 1,
                },
            ],
        }),
        [color]
    );
}

function RadioChannelUtilizationPanel() {
    const chartConfig = useChartConfig();
    const data = usePanelDataService(dataSource);
    const chartData = data?.data?.[0];

    if (!data) return null;

    return <PerformanceChart chartDef={chartConfig} data={chartData} />;
}

RadioChannelUtilizationPanel.propTypes = panelPropTypes;

RadioChannelUtilizationPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'RadioChannelUtilizationTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.RADIO,
        priority: PANEL_PRIORITIES.CHART,
        title,
        description: t`Channel utilization trend for a selected radio`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, radioFilterId],
        optional: [],
    },
})(RadioChannelUtilizationPanel);
